import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Plus, Check, Trash2 } from 'lucide-react';
import Toast, { ToastMessage } from '../common/Toast';
import Button from '../common/Button';
import Modal from '../common/Modal';
import FormControl from '../common/FormControl';
import NoDataFound from '../common/NoDataFound';
import FileAnalyticsAPI from '../file_analytics/fileAnalyticsService';
import {
  CollectionType, DataSource,
  ColDataType, ColMeasurementLevel, ColCardinalityHint, StandardColumn,
} from '../file_analytics/types';
import { maxLenForCode } from '../../validation/engine';

// ── Value lists ───────────────────────────────────────────────────────────────

const DATA_TYPES: { value: ColDataType; label: string }[] = [
  { value: 'text',       label: 'Text' },
  { value: 'integer',    label: 'Integer' },
  { value: 'decimal',    label: 'Decimal' },
  { value: 'date',       label: 'Date' },
  { value: 'datetime',   label: 'Date & Time' },
  { value: 'boolean',    label: 'Boolean' },
  { value: 'percentage', label: 'Percentage' },
];

const MEASUREMENT_LEVELS: { value: ColMeasurementLevel; label: string; hint: string }[] = [
  { value: 'nominal',  label: 'Nominal',  hint: 'Labels with no order (country, status)' },
  { value: 'ordinal',  label: 'Ordinal',  hint: 'Ordered categories, gaps undefined (rating, severity)' },
  { value: 'interval', label: 'Interval', hint: 'Equal gaps, no true zero (year, temperature °C)' },
  { value: 'ratio',    label: 'Ratio',    hint: 'Equal gaps, true zero (amount, count, duration)' },
];

const CARDINALITY_HINTS: { value: ColCardinalityHint; label: string; hint: string }[] = [
  { value: 'high_cardinality', label: 'High cardinality', hint: 'Unique or near-unique per row (IDs, names, timestamps)' },
  { value: 'low_cardinality',  label: 'Low cardinality',  hint: 'Small bounded set (status codes, flags, departments)' },
  { value: 'continuous',       label: 'Continuous',       hint: 'Unbounded numeric range (amounts, scores, durations)' },
  { value: 'binary',           label: 'Binary',           hint: 'Exactly two values (yes/no, true/false)' },
];

// ── Defaults ──────────────────────────────────────────────────────────────────

const EMPTY_COL: StandardColumn = {
  name: '', display_name: '',
  data_type: 'text', measurement_level: 'nominal', cardinality_hint: 'high_cardinality',
  required: false,
};

/** Normalise an incoming column — handles legacy {semantic_type} and new {data_type} shapes. */
const normaliseCol = (c: any): StandardColumn => {
  // New shape already has data_type
  if (c.data_type) {
    return {
      name:              c.name ?? '',
      display_name:      c.display_name ?? c.name ?? '',
      data_type:         c.data_type as ColDataType,
      measurement_level: c.measurement_level as ColMeasurementLevel ?? 'nominal',
      cardinality_hint:  c.cardinality_hint as ColCardinalityHint ?? 'high_cardinality',
      required:          !!c.required,
      description:       c.description,
    };
  }
  // Legacy shape: map semantic_type → 3 fields
  const st: string = c.semantic_type ?? '';
  let dt: ColDataType = 'text';
  let ml: ColMeasurementLevel = 'nominal';
  let ch: ColCardinalityHint = 'high_cardinality';
  if (st === 'numeric' || st === 'continuous') { dt = 'decimal'; ml = 'ratio'; ch = 'continuous'; }
  else if (st === 'categorical') { dt = 'text'; ml = 'nominal'; ch = 'low_cardinality'; }
  else if (st === 'date') { dt = 'date'; ml = 'interval'; ch = 'high_cardinality'; }
  else if (st === 'percent') { dt = 'percentage'; ml = 'ratio'; ch = 'continuous'; }
  return {
    name: c.name ?? '', display_name: c.display_name ?? c.name ?? '',
    data_type: dt, measurement_level: ml, cardinality_hint: ch,
    required: !!c.required, description: c.description,
  };
};

const cloneCols = (schema: StandardColumn[] | undefined): StandardColumn[] =>
  (schema ?? []).map(normaliseCol);

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  pipeline?: CollectionType;
  source?: DataSource;
  onSaved?: (ct: CollectionType) => void;
  onSourceSaved?: (ds: DataSource) => void;
  readOnly?: boolean;
}

export interface CmSchemaEditorHandle {
  addColumn: () => void;
  edit: () => void;
}

const CmSchemaEditor = forwardRef<CmSchemaEditorHandle, Props>(
  ({ pipeline, source, onSaved, onSourceSaved, readOnly = false }, ref) => {
    const item = source ?? pipeline;
    const [cols, setCols] = useState<StandardColumn[]>(() => cloneCols(item?.standard_schema));
    const [draft, setDraft] = useState<StandardColumn[]>([]);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const openEdit = (withNewRow = false) => {
      const base = cloneCols(cols);
      setDraft(withNewRow || base.length === 0 ? [...base, { ...EMPTY_COL }] : base);
      setOpen(true);
    };

    useImperativeHandle(ref, () => ({ addColumn: () => openEdit(true), edit: () => openEdit(false) }));

    const updateCol = (index: number, patch: Partial<StandardColumn>) =>
      setDraft(prev => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));

    const addColumn = () => setDraft(prev => [...prev, { ...EMPTY_COL }]);
    const removeColumn = (index: number) => setDraft(prev => prev.filter((_, i) => i !== index));

    const save = async (e: React.FormEvent) => {
      e.preventDefault();
      if (draft.some(c => !c.name.trim())) {
        setToast({ type: 'error', message: 'Every column needs a non-empty Column name.' });
        return;
      }
      setSaving(true);
      try {
        if (source) {
          const saved = await FileAnalyticsAPI.updateSource(source.id, { name: source.name, standard_schema: draft });
          setCols(cloneCols(saved.standard_schema));
          onSourceSaved?.(saved);
        } else if (pipeline) {
          const saved = await FileAnalyticsAPI.updateCollectionType(pipeline.id, { name: pipeline.name, standard_schema: draft });
          setCols(cloneCols(saved.standard_schema));
          onSaved?.(saved);
        }
        setOpen(false);
        setToast({ type: 'success', message: 'Schema saved.' });
      } catch (err: any) {
        setToast({ type: 'error', message: err?.response?.data?.detail || 'Failed to save schema.' });
      } finally {
        setSaving(false);
      }
    };

    const hasEmptyName = draft.some(c => !c.name.trim());

    const dtLabel   = (v: ColDataType)           => DATA_TYPES.find(x => x.value === v)?.label ?? v;
    const mlLabel   = (v: ColMeasurementLevel)    => MEASUREMENT_LEVELS.find(x => x.value === v)?.label ?? v;
    const chLabel   = (v: ColCardinalityHint)     => CARDINALITY_HINTS.find(x => x.value === v)?.label ?? v;

    return (
      <div className="netbody-content" data-testid="cm-schema-editor">
        <Toast toast={toast} onClose={() => setToast(null)} />

        <div className="scroll-container" style={readOnly ? { height: 'auto', maxHeight: 320, overflow: 'auto' } : undefined}>
          {cols.length === 0 ? (
            readOnly ? (
              <p className="collapse-section__empty">No columns defined.</p>
            ) : (
              <NoDataFound
                title="No columns defined"
                message="Add the columns this pipeline expects."
                action={
                  <Button variant="fill" icon={Plus} onClick={() => openEdit(true)} data-testid="BTN_cm_schema_add_empty">
                    Add Column
                  </Button>
                }
              />
            )
          ) : (
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Column name</th>
                      <th className="table-list-th">Display name</th>
                      <th className="table-list-th">Data type</th>
                      <th className="table-list-th">Measurement</th>
                      <th className="table-list-th">Cardinality</th>
                      <th className="table-list-th">Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cols.map((col, i) => (
                      <tr key={i} className="table-list-row" data-testid={`cm-schema-row-${i}`}>
                        <td className="table-list-cell table-list-cell--sno" style={{ textAlign: 'right', color: 'var(--text-tertiary)', fontSize: 11 }}>{i + 1}</td>
                        <td className="table-list-cell">
                          {col.name || <em style={{ color: 'var(--text-muted)' }}>—</em>}
                          {col.required && <span style={{ color: 'var(--red-600)', marginLeft: 2 }} aria-hidden="true">*</span>}
                        </td>
                        <td className="table-list-cell">{col.display_name || <em style={{ color: 'var(--text-muted)' }}>—</em>}</td>
                        <td className="table-list-cell">{dtLabel(col.data_type)}</td>
                        <td className="table-list-cell">{mlLabel(col.measurement_level)}</td>
                        <td className="table-list-cell">{chLabel(col.cardinality_hint)}</td>
                        <td className="table-list-cell">{col.required ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── Edit modal ── */}
        <Modal isOpen={open} onClose={() => setOpen(false)} size="xl">
          <Modal.Header>{`Edit Columns — "${source?.name ?? pipeline?.name ?? ''}"`}</Modal.Header>
          <Modal.Body>
            <form id="cm-schema-form" onSubmit={save}>
              <div className="table-list">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Column name</th>
                        <th className="table-list-th">Display name</th>
                        <th className="table-list-th">Data type</th>
                        <th className="table-list-th">Measurement</th>
                        <th className="table-list-th">Cardinality</th>
                        <th className="table-list-th">Required</th>
                        <th className="table-list-th table-list-th--right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.map((col, i) => (
                        <tr key={i} className="table-list-row" data-testid={`cm-schema-edit-row-${i}`}>
                          <td className="table-list-cell table-list-cell--sno" style={{ textAlign: 'right', color: 'var(--text-tertiary)', fontSize: 11 }}>{i + 1}</td>

                          <td className="table-list-cell">
                            <FormControl.Input
                              noMargin
                              value={col.name}
                              onChange={e => updateCol(i, { name: e.target.value })}
                              placeholder="e.g. invoice_no"
                              maxLength={maxLenForCode(109)}
                              data-testid={`INP_cm_schema_name_${i}`}
                            />
                          </td>

                          <td className="table-list-cell">
                            <FormControl.Input
                              noMargin
                              value={col.display_name}
                              onChange={e => updateCol(i, { display_name: e.target.value })}
                              placeholder="e.g. Invoice No."
                              maxLength={maxLenForCode(109)}
                              data-testid={`INP_cm_schema_display_${i}`}
                            />
                          </td>

                          <td className="table-list-cell">
                            <div style={{ minWidth: 120 }}>
                              <FormControl.SearchSelect
                                noMargin
                                value={col.data_type}
                                onChange={v => updateCol(i, { data_type: (v as ColDataType) ?? 'text' })}
                                options={DATA_TYPES}
                                data-testid={`SEL_cm_schema_dtype_${i}`}
                              />
                            </div>
                          </td>

                          <td className="table-list-cell">
                            <div style={{ minWidth: 130 }}>
                              <FormControl.SearchSelect
                                noMargin
                                value={col.measurement_level}
                                onChange={v => updateCol(i, { measurement_level: (v as ColMeasurementLevel) ?? 'nominal' })}
                                options={MEASUREMENT_LEVELS.map(x => ({ value: x.value, label: x.label }))}
                                data-testid={`SEL_cm_schema_ml_${i}`}
                              />
                            </div>
                          </td>

                          <td className="table-list-cell">
                            <div style={{ minWidth: 150 }}>
                              <FormControl.SearchSelect
                                noMargin
                                value={col.cardinality_hint}
                                onChange={v => updateCol(i, { cardinality_hint: (v as ColCardinalityHint) ?? 'high_cardinality' })}
                                options={CARDINALITY_HINTS.map(x => ({ value: x.value, label: x.label }))}
                                data-testid={`SEL_cm_schema_ch_${i}`}
                              />
                            </div>
                          </td>

                          <td className="table-list-cell">
                            <span
                              data-testid={`CHK_cm_schema_required_${i}`}
                              data-tooltip="Uploads missing this column are rejected."
                              data-tooltip-pos="bottom"
                              style={{ display: 'inline-flex' }}
                            >
                              <FormControl.Checkbox
                                noMargin
                                checked={col.required}
                                onChange={checked => updateCol(i, { required: checked })}
                              />
                            </span>
                          </td>

                          <td className="table-list-cell table-list-cell--actions">
                            <button
                              type="button"
                              className="btn-danger-outline-none"
                              onClick={() => removeColumn(i)}
                              title="Remove column"
                              aria-label="Remove column"
                              data-testid={`BTN_cm_schema_remove_${i}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button
                type="button"
                onClick={addColumn}
                data-testid="BTN_cm_schema_add"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  width: '100%', padding: '8px 0', marginTop: 10,
                  border: '1.5px dashed var(--brand-light-strong)',
                  borderRadius: 6, background: 'transparent', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--brand)',
                }}
              >
                <Plus size={13} />
                Add Column
              </button>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              form="cm-schema-form"
              variant="update"
              icon={Check}
              disabled={saving || hasEmptyName || draft.length === 0}
              data-testid="BTN_cm_schema_save"
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
);

CmSchemaEditor.displayName = 'CmSchemaEditor';
export default CmSchemaEditor;
